/*
 * Shared parent-origin handshake responder for every embed type on the page.
 *
 * Safari does not populate document.referrer for cross-origin iframes, so the player cannot see the
 * hosting page's origin to validate against a video's domain allowlist. The player postMessages
 * `{event:'requestParentOrigin', uuid}` to its parent and waits for a reply; this responder answers
 * via event.source with `{event:'captureParentOrigin', uuid}`, letting the player read the
 * browser-set event.origin. See IDEA-3100 / IDEA-4628 for more context.
 *
 * A single page-level `message` listener (registered once at bootstrap by the script and module
 * entry points) answers the handshake for both direct <iframe> embeds (the `iframe` embed-code
 * option, which have no VidyardPlayer instance) and <img> placeholder embeds. Replying to
 * event.source means the request is always answered in the frame that asked, regardless of how it
 * was embedded. It is purely a responder: it only ever replies to `requestParentOrigin` and ignores
 * every other message.
 */
import { getPlaybackURL } from './config';
import { addListener } from './embed-helpers';

declare global {
  interface Window {
    __vidyardParentOriginListening?: boolean;
  }
}

const handleMessage = (event: MessageEvent) => {
  // Only the player origin (honors VIDYARD_PLAYBACK_URL / data-playbackurl), matching the per-player handler.
  if (event.origin !== `https://${getPlaybackURL()}`) {
    return;
  }

  if (!event.source) {
    return;
  }

  if (typeof event.data !== 'string' || event.data === '') {
    return;
  }

  let data;
  try {
    data = JSON.parse(event.data);
  } catch (e) {
    return;
  }

  if (
    !data ||
    data.event !== 'requestParentOrigin' ||
    typeof data.uuid !== 'string' ||
    data.uuid.length === 0
  ) {
    return;
  }

  event.source.postMessage(
    JSON.stringify({ event: 'captureParentOrigin', uuid: data.uuid }),
    event.origin,
  );
};

/*
 * Register the page-level handshake responder exactly once. v4.js may be loaded more than once on a
 * page (each load re-runs the bundle in a fresh module scope), so the guard lives on window.
 */
const listenForParentOriginRequests = () => {
  if (window.__vidyardParentOriginListening) {
    return;
  }
  window.__vidyardParentOriginListening = true;
  addListener('message', 'onmessage', handleMessage, window);
};

export default listenForParentOriginRequests;
